/**
 * Keep only the cameras within `radiusM` of any of a set of points (e.g. the
 * GPS track currently loaded in Pathfinder). Uses a lat/lon grid so ~10k
 * cameras × ~50k track points stays interactive.
 */
import type { CameraFeatureCollection } from './cameraOverlayService';

export type LonLat = [number, number];

const EARTH_R = 6371000;

function haversineM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_R * Math.asin(Math.sqrt(Math.min(1, a)));
}

/** A point carrying an arbitrary payload (e.g. the live device it came from). */
export type TaggedPoint<T> = { lon: number; lat: number; tag: T };
export type NearbyMatch<T> = { tag: T; distanceM: number };

/** Spatial hash of points keyed by grid cell; cell size ≈ radius so a 3×3 probe covers the search disc. */
export class PointGrid<T = undefined> {
  private cells = new Map<string, TaggedPoint<T>[]>();
  private readonly cellDeg: number;
  private count = 0;

  constructor(points: Iterable<LonLat | TaggedPoint<T>>, radiusM: number) {
    // 1° latitude ≈ 111.32 km. Cells at least 1 m to avoid degenerate keys.
    this.cellDeg = Math.max(radiusM, 1) / 111_320;
    for (const raw of points) {
      const p: TaggedPoint<T> = Array.isArray(raw)
        ? { lon: raw[0], lat: raw[1], tag: undefined as unknown as T }
        : raw;
      if (!Number.isFinite(p.lon) || !Number.isFinite(p.lat)) continue;
      const key = this.key(p.lon, p.lat);
      const bucket = this.cells.get(key);
      if (bucket) bucket.push(p);
      else this.cells.set(key, [p]);
      this.count++;
    }
  }

  get size(): number {
    return this.count;
  }

  private key(lon: number, lat: number): string {
    return `${Math.floor(lat / this.cellDeg)}:${Math.floor(lon / this.cellDeg)}`;
  }

  /** Visit every indexed point in the cells that could contain a hit for (lon, lat). */
  private probe(lon: number, lat: number, visit: (p: TaggedPoint<T>) => boolean | void): void {
    const cy = Math.floor(lat / this.cellDeg);
    // Longitude cells shrink toward the poles; widen the probe accordingly.
    const lonScale = Math.max(0.2, Math.cos((lat * Math.PI) / 180));
    const cxSpan = Math.ceil(1 / lonScale);
    const cx = Math.floor(lon / this.cellDeg);
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -cxSpan; dx <= cxSpan; dx++) {
        const bucket = this.cells.get(`${cy + dy}:${cx + dx}`);
        if (!bucket) continue;
        for (const p of bucket) {
          if (visit(p) === true) return;
        }
      }
    }
  }

  /** True when any indexed point is within `radiusM` of (lon, lat). */
  hasWithin(lon: number, lat: number, radiusM: number): boolean {
    let hit = false;
    this.probe(lon, lat, (p) => {
      if (haversineM(lat, lon, p.lat, p.lon) <= radiusM) {
        hit = true;
        return true;
      }
      return undefined;
    });
    return hit;
  }

  /** All indexed points within `radiusM` of (lon, lat), nearest first. */
  within(lon: number, lat: number, radiusM: number): NearbyMatch<T>[] {
    const out: NearbyMatch<T>[] = [];
    this.probe(lon, lat, (p) => {
      const d = haversineM(lat, lon, p.lat, p.lon);
      if (d <= radiusM) out.push({ tag: p.tag, distanceM: d });
      return undefined;
    });
    out.sort((a, b) => a.distanceM - b.distanceM);
    return out;
  }
}

export type CameraNearby<T> = {
  feature: CameraFeatureCollection['features'][number];
  lon: number;
  lat: number;
  /** Tagged points within range, nearest first (never empty). */
  nearby: NearbyMatch<T>[];
};

/**
 * Like `filterCamerasNearPoints` but keeps *which* points are near each camera
 * (COP: label cameras by the live entities they cover). Returns the filtered
 * collection plus a parallel list of matches sorted by nearest point.
 */
export function annotateCamerasNearPoints<T>(
  cameras: CameraFeatureCollection,
  points: TaggedPoint<T>[],
  radiusM: number,
): { collection: CameraFeatureCollection; matches: CameraNearby<T>[] } {
  if (!points.length || !cameras.features.length) return { collection: cameras, matches: [] };
  const grid = new PointGrid<T>(points, radiusM);
  const matches: CameraNearby<T>[] = [];
  for (const feature of cameras.features) {
    const coords = feature.geometry?.coordinates;
    if (!Array.isArray(coords)) continue;
    const lon = Number(coords[0]);
    const lat = Number(coords[1]);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;
    const nearby = grid.within(lon, lat, radiusM);
    if (nearby.length) matches.push({ feature, lon, lat, nearby });
  }
  matches.sort((a, b) => a.nearby[0].distanceM - b.nearby[0].distanceM);
  return {
    collection: { type: 'FeatureCollection', features: matches.map((m) => m.feature) },
    matches,
  };
}

/**
 * Filter a camera collection down to cameras near any of `points` ([lon, lat]).
 * Returns the input untouched when there are no points.
 */
export function filterCamerasNearPoints(
  cameras: CameraFeatureCollection,
  points: LonLat[],
  radiusM: number,
): CameraFeatureCollection {
  if (!points.length || !cameras.features.length) return cameras;
  const grid = new PointGrid(points, radiusM);
  const features = cameras.features.filter((f) => {
    const coords = f.geometry?.coordinates;
    if (!Array.isArray(coords)) return false;
    const lon = Number(coords[0]);
    const lat = Number(coords[1]);
    return Number.isFinite(lon) && Number.isFinite(lat) && grid.hasWithin(lon, lat, radiusM);
  });
  return { type: 'FeatureCollection', features };
}

export const CAMERA_NEAR_TRACK_RADII_M = [50, 100, 250, 500] as const;
export type CameraNearTrackRadius = (typeof CAMERA_NEAR_TRACK_RADII_M)[number];

export type CameraNearTracksPrefs = {
  enabled: boolean;
  radiusM: CameraNearTrackRadius;
};

export const DEFAULT_CAMERA_NEAR_TRACKS: CameraNearTracksPrefs = { enabled: true, radiusM: 100 };

const NEAR_TRACKS_KEY = 'pathfinder.cameraOverlay.nearTracks.v1';
/** COP: cameras near live entities (same prefs shape, separate setting). */
export const COP_NEAR_ENTITIES_KEY = 'cop.cameraOverlay.nearEntities.v1';

/** COP shows every camera until the operator opts into "near entities". */
export const DEFAULT_COP_NEAR_ENTITIES: CameraNearTracksPrefs = { enabled: false, radiusM: 100 };

export function loadCameraNearTracksPrefs(
  storageKey: string = NEAR_TRACKS_KEY,
  defaults: CameraNearTracksPrefs = DEFAULT_CAMERA_NEAR_TRACKS,
): CameraNearTracksPrefs {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Partial<CameraNearTracksPrefs>;
    const radius = (CAMERA_NEAR_TRACK_RADII_M as readonly number[]).includes(Number(parsed.radiusM))
      ? (Number(parsed.radiusM) as CameraNearTrackRadius)
      : defaults.radiusM;
    return { enabled: parsed.enabled ?? defaults.enabled, radiusM: radius };
  } catch {
    return defaults;
  }
}

export function saveCameraNearTracksPrefs(prefs: CameraNearTracksPrefs, storageKey: string = NEAR_TRACKS_KEY): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(prefs));
  } catch {
    /* storage unavailable */
  }
}
